import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, InputNumber, Space, Typography, Alert, message } from 'antd';
import { getMqttMappingByMeter, mqttPublishByMeter, getMqttLatestMessage } from '../api';
import {
  getAdminMeterPublishKey,
  remotePayloadUseBleDirect,
  buildFinalPayload,
  getRxAddressCandidates,
} from '../utils/yjMeterRemote';

const { Text } = Typography;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// —— 以下组帧/解析与小程序 pages/test4/test4.js 一致（55 帧计量系数），仅写在管理端组件内，不改 yjMeterRemote ——

function assertRemoteAddress12(meterKey, isBleDirect) {
  const m = String(meterKey || '').trim().toLowerCase();
  if (isBleDirect) return m;
  if (!/^[0-9a-f]{12}$/.test(m)) {
    throw new Error('远程 55 帧需 12 位十六进制地址（4G 多为 device_mac 去冒号），请检查设备档案');
  }
  return m;
}

/** test4 remoteRead55Value('02')：表号 + 55 + 02 + 02 + 00000000 */
function buildReadMeteringFactorRawNoCrc(publishKey, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  return ((isBleDirect ? '' : m) + '55' + '02' + '02' + '00000000').toLowerCase();
}

/** test4 remoteSend55Config('meteringFactor', scaled)：scaled = Math.round(显示值 * 1000)，表号 + 55 + 02 + c2 + 8位hex */
function buildSetMeteringFactorRawNoCrc(publishKey, displayValue, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  const scaled = Math.round(Number(displayValue) * 1000);
  if (scaled < 0 || scaled > 0xffffffff) throw new Error('数值超出范围');
  const valueHex = (scaled >>> 0).toString(16).padStart(8, '0');
  return ((isBleDirect ? '' : m) + '55' + '02' + 'c2' + valueHex).toLowerCase();
}

/** test4 remoteSaveParams：表号 + 55 + 02 + 7e + 00000000 */
function buildSaveParamsRawNoCrc(publishKey, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  return ((isBleDirect ? '' : m) + '55' + '02' + '7e' + '00000000').toLowerCase();
}

/** test4 parseRx 55 分支：去 CRC 末 8 位为值；子命令在 16–17（0-based substring 16,18） */
function parse55MeteringFactorResponse(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 26) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '55' || msg.substring(14, 16) !== '02') return null;
  const subCmd = msg.substring(16, 18);
  if (subCmd !== '02' && subCmd !== 'c2') return null;
  const payloadNoCrc = msg.length > 2 ? msg.substring(0, msg.length - 2) : msg;
  const valueHex = payloadNoCrc.length >= 8 ? payloadNoCrc.substring(payloadNoCrc.length - 8) : '00000000';
  const valueDec = parseInt(valueHex || '0', 16) || 0;
  const valueFloat = Number((valueDec / 1000).toFixed(3));
  return { subCmd, valueHex, valueFloat };
}

function parse55SaveConfirm(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 26) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '55' || msg.substring(14, 16) !== '02') return null;
  const subCmd = msg.substring(16, 18);
  const subDec = parseInt(subCmd, 16);
  if (subCmd === '7e' || (Number.isFinite(subDec) && (subDec & 0x3f) === 0x3e)) {
    return { subCmd };
  }
  return null;
}

/** 设置计量系数下发后：设备回调子命令应为 c2（与 test4 55 解析一致） */
function parse55C2SetConfirm(rawMsg, addressCandidates) {
  const parsed = parse55MeteringFactorResponse(rawMsg, addressCandidates);
  if (parsed && parsed.subCmd === 'c2') return parsed;
  return null;
}

const POLL_MS_DEFAULT = 10000;

/**
 * 4G YJ 表「计量系数」：与 test4 读取/设置/保存参数相同 MQTT 流程；仅 PC 管理端，不修改 yjMeterRemote.js。
 */
const MeterMeteringFactorModal = ({ open, device, variant, onClose }) => {
  const [readLoading, setReadLoading] = useState(false);
  const [readValue, setReadValue] = useState(null);
  const [readHint, setReadHint] = useState('');
  const [readError, setReadError] = useState('');

  const [coeffInput, setCoeffInput] = useState(null);
  const [setLoading, setSetLoading] = useState(false);
  const [setHint, setSetHint] = useState('');
  const [setError, setSetError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveHint, setSaveHint] = useState('');
  const [saveError, setSaveError] = useState('');

  const isWater = variant === 'water';
  const deviceKind = isWater ? '水表' : '电表';

  const resetState = useCallback(() => {
    setReadLoading(false);
    setReadValue(null);
    setReadHint('');
    setReadError('');
    setCoeffInput(null);
    setSetLoading(false);
    setSetHint('');
    setSetError('');
    setSaveLoading(false);
    setSaveHint('');
    setSaveError('');
  }, []);

  useEffect(() => {
    if (open) resetState();
  }, [open, device?.id, resetState]);

  const publishAndPollMeteringRead = async (rxTopic, candidates, timeoutMs = POLL_MS_DEFAULT) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const msgRes = await getMqttLatestMessage(rxTopic, 'raw');
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      if (raw) {
        const parsed = parse55MeteringFactorResponse(String(raw), candidates);
        if (parsed) return parsed;
      }
      await sleep(400);
    }
    return null;
  };

  const pollAfterSetC2 = async (rxTopic, candidates, timeoutMs = POLL_MS_DEFAULT) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const msgRes = await getMqttLatestMessage(rxTopic, 'raw');
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      if (raw) {
        const parsed = parse55C2SetConfirm(String(raw), candidates);
        if (parsed) return parsed;
      }
      await sleep(400);
    }
    return null;
  };

  const pollSaveConfirm = async (rxTopic, candidates, timeoutMs = POLL_MS_DEFAULT) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const msgRes = await getMqttLatestMessage(rxTopic, 'raw');
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      if (raw) {
        const info = parse55SaveConfirm(String(raw), candidates);
        if (info) return info;
      }
      await sleep(400);
    }
    return null;
  };

  const handleReadMeteringFactor = async () => {
    if (!device) return;
    setReadLoading(true);
    setReadError('');
    setReadHint('正在下发读取计量系数指令…');
    setReadValue(null);
    try {
      const publishKey = getAdminMeterPublishKey(device);
      if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');

      const mapRes = await getMqttMappingByMeter(publishKey);
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildReadMeteringFactorRawNoCrc(publishKey, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }

      setReadHint('已下发，等待设备回调（最多约 10 秒）…');
      const candidates = getRxAddressCandidates(device);
      const parsed = await publishAndPollMeteringRead(rxTopic, candidates, POLL_MS_DEFAULT);
      if (!parsed) {
        setReadHint('');
        setReadError('读取失败：超时未收到有效 55 回调（子命令须为 02 或 c2）');
        return;
      }
      setReadValue(parsed.valueFloat);
      setReadHint('已收到表计回调');
      message.success(`当前计量系数：${parsed.valueFloat}`);
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '读取失败';
      setReadHint('');
      setReadError(errMsg);
    } finally {
      setReadLoading(false);
    }
  };

  const handleSetMeteringFactor = async () => {
    if (!device) return;
    const v = Number(coeffInput);
    if (coeffInput == null || Number.isNaN(v) || v < 0) {
      message.warning('请输入有效非负数值（与 test4 一致：内部按 ×1000 取整下发）');
      return;
    }
    setSetLoading(true);
    setSetError('');
    setSetHint('');
    try {
      const publishKey = getAdminMeterPublishKey(device);
      if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');

      const mapRes = await getMqttMappingByMeter(publishKey);
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const candidates = getRxAddressCandidates(device);

      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildSetMeteringFactorRawNoCrc(publishKey, v, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }

      setSetHint('已下发设置指令，等待设备 c2 回调（最多约 10 秒）…');
      const echoed = await pollAfterSetC2(rxTopic, candidates, POLL_MS_DEFAULT);
      if (echoed) {
        setReadValue(echoed.valueFloat);
        setSetHint(
          `设备已回包确认（子命令 c2），解析计量系数 ${echoed.valueFloat}。请再点「保存参数」写入表内存储。`
        );
        message.success(`设置已得到表计 c2 回调：${echoed.valueFloat}`);
      } else {
        setSetHint('');
        setSetError(
          '指令已下发，但未在超时内收到 c2 回调（可能离线或缓存未更新）。请稍后点「读取计量系数」核对；若仍不符请检查表是否在线。'
        );
        message.warning('未收到 c2 确认，请用「读取计量系数」核对');
      }
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '设置失败';
      setSetHint('');
      setSetError(errMsg);
      Modal.error({ title: '设置计量系数失败', content: errMsg });
    } finally {
      setSetLoading(false);
    }
  };

  const handleSaveParams = async () => {
    if (!device) return;
    setSaveLoading(true);
    setSaveError('');
    setSaveHint('');
    try {
      const publishKey = getAdminMeterPublishKey(device);
      if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');

      const mapRes = await getMqttMappingByMeter(publishKey);
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildSaveParamsRawNoCrc(publishKey, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '保存指令发送失败');
      }
      const candidates = getRxAddressCandidates(device);
      setSaveHint('已下发保存指令，等待 7e 类确认（最多约 10 秒）…');
      const saveInfo = await pollSaveConfirm(rxTopic, candidates, POLL_MS_DEFAULT);
      if (saveInfo) {
        setSaveHint(`设备已回包确认保存（子命令 ${saveInfo.subCmd}）。建议再点「读取计量系数」确认落盘值。`);
        message.success('保存参数已得到表计确认');
      } else {
        setSaveHint('');
        setSaveError(
          '保存指令已下发，但未在超时内收到 7e 类确认。请稍后点「读取计量系数」核对是否已写入；若未变请重试或检查表在线。'
        );
        message.warning('未收到保存确认，请用「读取计量系数」核对');
      }
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '保存失败';
      setSaveHint('');
      setSaveError(errMsg);
      Modal.error({ title: '保存参数失败', content: errMsg });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Modal
      title={`计量系数 — ${device?.current_meter_no || device?.id || ''}（${deviceKind}）`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
      ]}
      width={520}
      destroyOnClose
      maskClosable={false}
    >
      {device && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="与小程序 test4 一致"
            description={
              <span>
                读取：<Text code>55 02 02 00000000</Text>；设置：<Text code>55 02 c2</Text> + 数值×1000 的 8
                位十六进制；保存：<Text code>55 02 7e 00000000</Text>。回调值 = 报文末 8 位 hex 整数 ÷ 1000。
              </span>
            }
          />

          {readError && <Alert type="error" message={readError} showIcon />}
          {readHint && !readError && (
            <Alert type={readHint.includes('已收到') ? 'success' : 'info'} message={readHint} showIcon />
          )}

          <div>
            <Text strong>读取计量系数</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">表计回调解析值（÷1000）</Text>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 6 }}>
                {readValue != null && !Number.isNaN(readValue) ? readValue.toFixed(3) : '—'}
              </div>
            </div>
            <Button type="primary" style={{ marginTop: 10 }} loading={readLoading} onClick={handleReadMeteringFactor}>
              读取计量系数
            </Button>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text strong>设置计量系数</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap align="center">
                <span>系数</span>
                <InputNumber
                  min={0}
                  step={0.001}
                  precision={3}
                  value={coeffInput}
                  onChange={setCoeffInput}
                  placeholder="如 1.000"
                  style={{ width: 140 }}
                />
                <Button type="primary" loading={setLoading} onClick={handleSetMeteringFactor}>
                  设置计量系数
                </Button>
              </Space>
            </div>
            {setError && (
              <Alert type="warning" message={setError} showIcon style={{ marginTop: 10 }} />
            )}
            {setHint && !setError && (
              <Alert
                type={setHint.includes('已回包确认') ? 'success' : 'info'}
                message={setHint}
                showIcon
                style={{ marginTop: 10 }}
              />
            )}
            <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>
              与「补电量」弹窗类似：设置后会轮询 RX 等 c2 回包；若超时请用「读取计量系数」核对。保存参数后同样建议再读一次确认落盘。
            </Text>
            <div style={{ marginTop: 12 }}>
              <Text strong>保存参数</Text>
            </div>
            {saveError && (
              <Alert type="warning" message={saveError} showIcon style={{ marginTop: 8 }} />
            )}
            {saveHint && !saveError && (
              <Alert
                type={saveHint.includes('已回包确认') ? 'success' : 'info'}
                message={saveHint}
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
            <Button style={{ marginTop: 8 }} loading={saveLoading} onClick={handleSaveParams}>
              保存参数
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default MeterMeteringFactorModal;
